import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { getStore, saveStore } from "../storage/hearingTestStore.js";
import { createEmptyPatientDraft } from "../models/patientDraft.js";

const PatientContext = createContext();

export function PatientProvider({ children }) {
  const [patientDraft, setPatientDraft] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const skipFirstSave = useRef(false);
  // initialize local storage key on start up
  useEffect(() => {
    const store = getStore();
    if (store) {
      setPatientDraft(store.patientDraft);
      setUploadQueue(store.uploadQueue);
    }
    setIsInitialized(true);
  }, []);
  // store data to local storage whenever our queue or patient draft changes.
  useEffect(() => {
    if (skipFirstSave.current === false) {
      skipFirstSave.current = true;
      return;
    }
    saveStore({ patientDraft, uploadQueue });
  }, [patientDraft, uploadQueue]);

  function resetDraft() {
    setPatientDraft(createEmptyPatientDraft());
  }

  function updateDemographics(patch) {
    setPatientDraft((prev) => {
      const draft = prev ?? createEmptyPatientDraft();
      return {
        ...draft,
        demographics: {
          ...draft.demographics,
          ...patch,
        },
      };
    });
  }

  function updateTest(patch) {
    setPatientDraft((prev) => {
      const draft = prev ?? createEmptyPatientDraft();
      return {
        ...draft,
        test: {
          ...draft.test,
          ...patch,
        },
      };
    });
  }

  function finalizeAndEnqueue(testPatch) {
    // making sure our patientDraft demographics isnt empty when uploading to queue.
    if (patientDraft.demographics.first_name === "") {
      return;
    }
    setUploadQueue((prevQueue) => {
      const completedDraft = {
        ...patientDraft,
        test: {
          ...patientDraft.test,
          ...testPatch,
        },
      };
      return [...prevQueue, completedDraft];
    });
  }
  function deleteFromUploadQueue(index) {
    setUploadQueue((prevQueue) => {
      return prevQueue.filter((_, i) => i !== index);
    });
  }

  function getPatientName() {
    if (!patientDraft || !patientDraft.demographics) return "";
    const { first_name, last_name } = patientDraft.demographics;
    return [first_name, last_name].filter(Boolean).join(" ");
  }

  const value = {
    patientDraft,
    uploadQueue,
    isInitialized,
    finalizeAndEnqueue,
    deleteFromUploadQueue,
    updateDemographics,
    updateTest,
    resetDraft,
    getPatientName
  };
  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
}

export function usePatient() {
  const ctx = useContext(PatientContext);
  if (!ctx) throw new Error("usePatient must be used inside PatientProvider");
  return ctx;
}

